import { formatDateInput, isDateClosed } from './booking'

export type BookingStatus = 'pending' | 'confirmed' | 'cancelled'

export interface Booking {
  id: string
  name: string
  phone: string
  email: string
  guests: number
  date: string
  time: string
  notes: string
  status: BookingStatus
  locale: 'it' | 'en'
  createdAt: string
}

const BOOKINGS_KEY = 'maia-bookings'
const LAST_BOOKING_KEY = 'maia-last-booking-id'
const DEMO_CONFIRM_KEY_PREFIX = 'maia-demo-confirmed-'
const DEMO_CANCELLED_KEY_PREFIX = 'maia-demo-cancelled-'
const DEMO_HIDDEN_KEY_PREFIX = 'maia-demo-hidden-'

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function readBookings(): Booking[] {
  try {
    const raw = localStorage.getItem(BOOKINGS_KEY)
    return raw ? (JSON.parse(raw) as Booking[]) : []
  } catch {
    return []
  }
}

function writeBookings(bookings: Booking[]): void {
  localStorage.setItem(BOOKINGS_KEY, JSON.stringify(bookings))
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

const DEMO_TEMPLATES = [
  {
    id: 'demo-1',
    name: 'Mario Rossi',
    phone: '+39 333 123 4567',
    email: 'mario.rossi@example.com',
    guests: 4,
    offset: 0,
    time: '12:30',
    notes: 'Tavolo vicino alla finestra',
    locale: 'it' as const,
    status: 'pending' as const,
  },
  {
    id: 'demo-2',
    name: 'Anna Bianchi',
    phone: '+39 347 765 4321',
    email: 'anna.bianchi@example.com',
    guests: 2,
    offset: 0,
    time: '19:30',
    notes: '',
    locale: 'it' as const,
    status: 'confirmed' as const,
  },
  {
    id: 'demo-3',
    name: 'Marco Neri',
    phone: '+39 320 444 5566',
    email: 'marco.neri@example.com',
    guests: 3,
    offset: 0,
    time: '20:30',
    notes: 'Fuori sede',
    locale: 'it' as const,
    status: 'pending' as const,
  },
  {
    id: 'demo-4',
    name: 'John Smith',
    phone: '+44 7700 900001',
    email: 'john.smith@example.com',
    guests: 6,
    offset: 1,
    time: '13:00',
    notes: 'Cena di compleanno',
    locale: 'en' as const,
    status: 'confirmed' as const,
  },
  {
    id: 'demo-5',
    name: 'Laura Bianchi',
    phone: '+39 338 777 8899',
    email: 'laura.bianchi@example.com',
    guests: 2,
    offset: 1,
    time: '19:00',
    notes: '',
    locale: 'it' as const,
    status: 'pending' as const,
  },
  {
    id: 'demo-6',
    name: 'Giulia Verdi',
    phone: '+39 320 111 2233',
    email: 'giulia.verdi@example.com',
    guests: 3,
    offset: 2,
    time: '20:00',
    notes: '',
    locale: 'it' as const,
    status: 'cancelled' as const,
  },
  {
    id: 'demo-7',
    name: 'Sophie Martin',
    phone: '+33 6 12 34 56 78',
    email: 'sophie.martin@example.com',
    guests: 5,
    offset: 3,
    time: '12:30',
    notes: 'Tavolo per famiglia',
    locale: 'en' as const,
    status: 'confirmed' as const,
  },
  {
    id: 'demo-8',
    name: 'Paolo Rossi',
    phone: '+39 347 222 3344',
    email: 'paolo.rossi@example.com',
    guests: 2,
    offset: 3,
    time: '12:30',
    notes: '',
    locale: 'it' as const,
    status: 'pending' as const,
  },
  {
    id: 'demo-9',
    name: 'Luca Ferrari',
    phone: '+39 338 999 8877',
    email: 'luca.ferrari@example.com',
    guests: 2,
    offset: 5,
    time: '19:30',
    notes: 'Allergie: noci',
    locale: 'it' as const,
    status: 'pending' as const,
  },
  {
    id: 'demo-10',
    name: 'Elena Green',
    phone: '+39 329 555 6677',
    email: 'elena.green@example.com',
    guests: 4,
    offset: 5,
    time: '20:00',
    notes: '',
    locale: 'en' as const,
    status: 'cancelled' as const,
  },
  {
    id: 'demo-11',
    name: 'Roberto Blu',
    phone: '+39 366 888 9900',
    email: 'roberto.blu@example.com',
    guests: 3,
    offset: 6,
    time: '12:30',
    notes: '',
    locale: 'it' as const,
    status: 'pending' as const,
  },
]

function isDemoConfirmedToday(id: string, date: string): boolean {
  try {
    return localStorage.getItem(`${DEMO_CONFIRM_KEY_PREFIX}${id}`) === date
  } catch {
    return false
  }
}

function setDemoConfirmed(id: string, date: string): void {
  localStorage.setItem(`${DEMO_CONFIRM_KEY_PREFIX}${id}`, date)
}

function isDemoCancelledToday(id: string, date: string): boolean {
  try {
    return localStorage.getItem(`${DEMO_CANCELLED_KEY_PREFIX}${id}`) === date
  } catch {
    return false
  }
}

function setDemoCancelled(id: string, date: string): void {
  localStorage.setItem(`${DEMO_CANCELLED_KEY_PREFIX}${id}`, date)
}

function clearDemoCancelled(id: string): void {
  try {
    localStorage.removeItem(`${DEMO_CANCELLED_KEY_PREFIX}${id}`)
  } catch {
    // ignore
  }
}

function isDemoHiddenToday(id: string, date: string): boolean {
  try {
    return localStorage.getItem(`${DEMO_HIDDEN_KEY_PREFIX}${id}`) === date
  } catch {
    return false
  }
}

function setDemoHidden(id: string, date: string): void {
  localStorage.setItem(`${DEMO_HIDDEN_KEY_PREFIX}${id}`, date)
}

function getDemoBookings(): Booking[] {
  const today = new Date()
  const visible = DEMO_TEMPLATES.map((template) => {
    let date = addDays(today, template.offset)
    while (isDateClosed(date)) {
      date = addDays(date, 1)
    }
    const dateStr = formatDateInput(date)
    return { template, date: dateStr }
  }).filter(({ template, date }) => !isDemoHiddenToday(template.id, date))

  return visible.map(({ template, date }) => {
    let status = template.status
    if (isDemoCancelledToday(template.id, date)) {
      status = 'cancelled'
    } else if (
      status === 'confirmed' ||
      isDemoConfirmedToday(template.id, date)
    ) {
      status = 'confirmed'
    }
    return {
      id: template.id,
      name: template.name,
      phone: template.phone,
      email: template.email,
      guests: template.guests,
      date,
      time: template.time,
      notes: template.notes,
      status,
      locale: template.locale,
      createdAt: today.toISOString(),
    }
  })
}

export function getBookings(): Booking[] {
  return [...getDemoBookings(), ...readBookings()].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

export function getBooking(id: string): Booking | undefined {
  return getBookings().find((b) => b.id === id)
}

export function saveBooking(
  data: Omit<Booking, 'id' | 'status' | 'createdAt'>
): Booking {
  const booking: Booking = {
    ...data,
    id: generateId(),
    status: 'pending',
    createdAt: new Date().toISOString(),
  }
  writeBookings([booking, ...readBookings()])
  setLastBookingId(booking.id)
  return booking
}

export function confirmBooking(id: string): Booking | null {
  if (id.startsWith('demo-')) {
    const demo = getDemoBookings().find((b) => b.id === id)
    if (!demo) return null
    setDemoConfirmed(id, demo.date)
    clearDemoCancelled(id)
    return { ...demo, status: 'confirmed' }
  }

  const bookings = readBookings()
  const index = bookings.findIndex((b) => b.id === id)
  if (index === -1) return null

  bookings[index] = { ...bookings[index], status: 'confirmed' }
  writeBookings(bookings)
  return bookings[index]
}

export function cancelBooking(id: string): Booking | null {
  if (id.startsWith('demo-')) {
    const demo = getDemoBookings().find((b) => b.id === id)
    if (!demo) return null
    setDemoCancelled(id, demo.date)
    try {
      localStorage.removeItem(`${DEMO_CONFIRM_KEY_PREFIX}${id}`)
    } catch {
      // ignore
    }
    return { ...demo, status: 'cancelled' }
  }

  const bookings = readBookings()
  const index = bookings.findIndex((b) => b.id === id)
  if (index === -1) return null

  bookings[index] = { ...bookings[index], status: 'cancelled' }
  writeBookings(bookings)
  return bookings[index]
}

export function deleteBooking(id: string): Booking | null {
  if (id.startsWith('demo-')) {
    const demo = getDemoBookings().find((b) => b.id === id)
    if (!demo) return null
    setDemoHidden(id, demo.date)
    clearDemoCancelled(id)
    try {
      localStorage.removeItem(`${DEMO_CONFIRM_KEY_PREFIX}${id}`)
    } catch {
      // ignore
    }
    return demo
  }

  const bookings = readBookings()
  const index = bookings.findIndex((b) => b.id === id)
  if (index === -1) return null

  const [removed] = bookings.splice(index, 1)
  writeBookings(bookings)
  return removed
}

export function setLastBookingId(id: string): void {
  localStorage.setItem(LAST_BOOKING_KEY, id)
}

export function getLastBookingId(): string | null {
  try {
    return localStorage.getItem(LAST_BOOKING_KEY)
  } catch {
    return null
  }
}

export function clearLastBookingId(): void {
  localStorage.removeItem(LAST_BOOKING_KEY)
}

export function clearAllBookings(): void {
  localStorage.removeItem(BOOKINGS_KEY)
  localStorage.removeItem(LAST_BOOKING_KEY)
  DEMO_TEMPLATES.forEach(({ id }) => {
    localStorage.removeItem(`${DEMO_CONFIRM_KEY_PREFIX}${id}`)
    localStorage.removeItem(`${DEMO_CANCELLED_KEY_PREFIX}${id}`)
    localStorage.removeItem(`${DEMO_HIDDEN_KEY_PREFIX}${id}`)
  })
}
